'use client';

import { c as _c } from "react/compiler-runtime";
import { CardElement, IbanElement } from '@stripe/react-stripe-js';
import { ConfirmCardPaymentData, ConfirmSepaDebitPaymentData } from '@stripe/stripe-js';
import { PaymentMethods } from 'api-graphql-types';
import { useCallback } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { type processDonationFormAction, processDonationAction } from '../../../operations/processDonationAction';
import { type GenericFinalizeProps, type StripeFinalizeProps } from './types';
import type { DonationFormValues, StripeOptions } from '@/donationForm/types';
import { useErrorHandler } from '@/donationPages/_dependencies/helpers';
import withThrottle from '@/donationPages/_dependencies/helpers/useDonationForm/useFinalizeHandler/withThrottle';
import useRedirectHandler from '@/donationPages/_dependencies/helpers/useRedirectHandler';
import type { RedirectHandler } from '@/donationPages/_dependencies/helpers/useRedirectHandler/types';
import { useStripeContext } from '@/donationPages/_dependencies/StripeContext';
import { report } from '@/errorReporting';
import type { ErrorHandler, OnAfterSubmitSuccess, SetterTools } from '@/form/ActionForm/useActionForm';
import type { OperationReturn } from '@/helpers/operations';
import { getTranslatorWithFallback } from '@/i18n';
import { NextIntlKeys, Translator, type Locale } from '@/i18n/types';
import { finalizePaymentAction } from '@/donationPages/_dependencies/operations/finalizePaymentAction';
import { useLocale } from '@/i18n/client';
const submitHandler = async ({
  donationId,
  redirectTo,
  stripePaymentIntentClientSecret: paymentIntentClientSecret,
  donationToken
}: NonNullable<OperationReturn<typeof processDonationAction>['data']>, values: DonationFormValues, tools: SetterTools<DonationFormValues>, stripeOptions: StripeOptions, redirect: RedirectHandler, handleError: ErrorHandler<DonationFormValues>, t_: Translator, locale: Locale) => {
  const t = getTranslatorWithFallback(t_);
  async function errorHandler(errors: FieldErrors<DonationFormValues>) {
    await handleError(errors, values, tools, {
      shouldSetErrors: true
    });
  }
  if (!redirectTo) {
    report(new TypeError('Backend did not return "redirectTo" for this request'));
    return await errorHandler({
      payment_method: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
  if (!donationId) {
    report(new TypeError('Backend did not return "donationId" for this request'));
    return await errorHandler({
      payment_method: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
  if (!donationToken) {
    report(new TypeError('Backend did not return "donationToken" for this request'));
    return await errorHandler({
      payment_method: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
  const throttle = withThrottle(() => errorHandler({
    payment_method: {
      message: t('nextjs.donate.errors.throttled'),
      type: 'validate'
    }
  }));
  switch (values.payment_method) {
    case PaymentMethods.StripeCc:
      return await throttle(stripeCCFinalize)({
        t,
        values,
        redirect,
        redirectTo,
        donationId,
        donationToken,
        stripeOptions,
        errorHandler,
        paymentIntentClientSecret,
        locale
      });
    case PaymentMethods.StripeSepaDebit:
      return await throttle(stripeSEPADebitFinalize)({
        t,
        values,
        redirect,
        redirectTo,
        donationId,
        donationToken,
        stripeOptions,
        errorHandler,
        paymentIntentClientSecret,
        locale
      });
    case PaymentMethods.StripeWero:
      weroFinalize(redirectTo);
      return;
    case PaymentMethods.StripeApplePay:
    case PaymentMethods.StripeGooglePay:
    case PaymentMethods.Paypal:
      return;
    default:
      return otherFinalize({
        redirect,
        redirectTo,
        donationId,
        donationToken,
        t,
        errorHandler,
        values,
        locale
      });
  }
};
async function stripeCCFinalize({
  stripeOptions,
  values,
  errorHandler,
  donationId,
  donationToken,
  redirect,
  paymentIntentClientSecret,
  t,
  locale,
  redirectTo
}: StripeFinalizeProps) {
  if (!paymentIntentClientSecret) {
    report(new TypeError('The backend has not returned a valid stripe token for this request'));
    return await errorHandler({
      stripe_token: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
  const card = stripeOptions?.elements?.getElement(CardElement);
  if (!card || typeof stripeOptions?.stripe?.confirmCardPayment !== 'function') {
    // show generic error message if the card element doesn't exist
    return await errorHandler({
      stripe_token: {
        message: t('nextjs.donate.errors.stripe_cc_unavailable'),
        type: 'validate'
      }
    });
  }
  const {
    first_name,
    last_name
  } = values;
  const data: ConfirmCardPaymentData = {
    payment_method: {
      card,
      billing_details: {
        name: `${first_name} ${last_name}`
      }
    }
  };
  try {
    const result = await stripeOptions.stripe.confirmCardPayment(paymentIntentClientSecret, data);
    const error = result.error;
    if (error && !/already succeeded/.test(error.message ?? '')) {
      const message = t(`nextjs.donate.errors.stripe_cc_${error.code ?? 'unknown_error'}` as NextIntlKeys, {
        defaultValue: error.message ?? t('nextjs.donate.errors.unknown_error')
      });
      return await errorHandler({
        stripe_token: {
          message,
          type: 'validate'
        }
      });
    }
    const {
      error: finalizePaymentError
    } = await finalizePaymentAction(donationToken, locale);
    if (finalizePaymentError) {
      const message = t('nextjs.donate.errors.stripe_cc_unknown_error');
      return await errorHandler({
        stripe_token: {
          message,
          type: 'validate'
        }
      });
    }
    await redirect(redirectTo, donationId, values);
  } catch (err) {
    report(err);
    await errorHandler({
      stripe_token: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
}
async function stripeSEPADebitFinalize({
  stripeOptions,
  values,
  errorHandler,
  donationId,
  donationToken,
  redirect,
  paymentIntentClientSecret,
  t,
  locale,
  redirectTo
}: StripeFinalizeProps) {
  if (!paymentIntentClientSecret) {
    report(new TypeError('The backend has not returned a valid stripe token for this request'));
    return await errorHandler({
      stripe_token: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
  const sepa_debit = stripeOptions?.elements?.getElement(IbanElement);
  if (!sepa_debit || typeof stripeOptions?.stripe?.confirmSepaDebitPayment !== 'function') {
    // show generic error message if the iban element doesn't exist
    return await errorHandler({
      stripe_token: {
        message: t('nextjs.donate.errors.stripe_sepa_debit_unavailable'),
        type: 'validate'
      }
    });
  }
  const {
    first_name,
    last_name,
    _company_donation,
    company_name,
    email,
    city,
    zip
  } = values;
  const data: ConfirmSepaDebitPaymentData = {
    payment_method: {
      sepa_debit,
      billing_details: {
        // both first_name, last_name and email will be there due to schema validation
        name: `${first_name} ${last_name}`,
        email: email!,
        // make sure that a given company_name appears in the sepa mandate that is generated by Stripe
        address: _company_donation && company_name ? {
          city,
          line1: company_name,
          postal_code: zip
        } : undefined
      }
    }
  };
  try {
    const result = await stripeOptions.stripe.confirmSepaDebitPayment(paymentIntentClientSecret, data);
    const error = result.error;
    if (error && !/already succeeded/.test(error.message ?? '')) {
      const message = t(`nextjs.donate.errors.stripe_sepa_debit_${error.code ?? 'unknown_error'}` as NextIntlKeys, {
        defaultValue: error.message ?? t('nextjs.donate.errors.unknown_error')
      });
      return await errorHandler({
        stripe_token: {
          message,
          type: 'validate'
        }
      });
    }
    const {
      error: finalizePaymentError
    } = await finalizePaymentAction(donationToken, locale);
    if (finalizePaymentError) {
      const message = t('nextjs.donate.errors.stripe_cc_unknown_error');
      return await errorHandler({
        stripe_token: {
          message,
          type: 'validate'
        }
      });
    }
    await redirect(redirectTo, donationId, values);
  } catch (err) {
    report(err);
    await errorHandler({
      stripe_token: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
}
async function otherFinalize({
  redirect,
  values,
  donationId,
  donationToken,
  t,
  errorHandler,
  locale,
  redirectTo
}: GenericFinalizeProps) {
  try {
    const {
      error
    } = await finalizePaymentAction(donationToken, locale);
    if (error) {
      const message = t('nextjs.donate.errors.stripe_cc_unknown_error');
      return await errorHandler({
        stripe_token: {
          message,
          type: 'validate'
        }
      });
    }
    return await redirect(redirectTo, donationId, values);
  } catch (error) {
    report(error);
    await errorHandler({
      stripe_token: {
        message: t('nextjs.donate.errors.unknown_error'),
        type: 'validate'
      }
    });
  }
}
function weroFinalize(redirectTo: string) {
  window.location.assign(redirectTo);
}
function useFinalizeHandler(t) {
  const $ = _c(6);
  const stripeOptions = useStripeContext();
  const redirect = useRedirectHandler();
  const errorHandler = useErrorHandler();
  const locale = useLocale();
  let t0;
  if ($[0] !== errorHandler || $[1] !== locale || $[2] !== redirect || $[3] !== stripeOptions || $[4] !== t) {
    t0 = (data, input, tools) => submitHandler(data, input, tools, stripeOptions, redirect, errorHandler, t, locale);
    $[0] = errorHandler;
    $[1] = locale;
    $[2] = redirect;
    $[3] = stripeOptions;
    $[4] = t;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const handler = t0;
  return handler;
}
export default useFinalizeHandler;